import { useState } from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { ChevronLeft } from "lucide-react";
import { Post } from "@/types/posts";

interface PostShareSheetProps {
  post: Post;
  isOpen: boolean;
  onClose: () => void;
}

export function PostShareSheet({ post, isOpen, onClose }: PostShareSheetProps) {
  const [showUserList, setShowUserList] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // Mock user data với avatar từ thư mục avatars
  const mockUsers = [
    { id: "1", name: "Ponyo", avatar: "/avatars/ponyo.svg" },
    { id: "2", name: "Chihiro", avatar: "/avatars/avatar.svg" },
    { id: "3", name: "Haku", avatar: "/avatars/haku.svg" },
    { id: "4", name: "Marnie", avatar: "/avatars/marnie.svg" },
    { id: "5", name: "Kiki", avatar: "/avatars/kiki.svg" },
    { id: "6", name: "Vô diện", avatar: "/avatars/vodien.svg" },
    { id: "7", name: "Hakura", avatar: "/avatars/haku.svg" },
  ];

  // Filtered users based on search
  const filteredUsers = searchQuery
    ? mockUsers.filter((user) =>
        user.name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : mockUsers;

  const handleUserToggle = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleShareClick = () => {
    onClose();
    // Here you would implement the actual share functionality
    console.log("Shared post", post.id, "to users:", selectedUsers);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="bottom"
        className="bg-white rounded-t-xl max-h-[80vh] overflow-y-auto"
      >
        {showUserList ? (
          <div className="flex flex-col h-full bg-[#FBF6F1]">
            <div className="flex items-center justify-between px-4 py-3">
              <button className="p-1" onClick={() => setShowUserList(false)}>
                <ChevronLeft className="w-5 h-5 text-[#1E3A3A]" />
              </button>
              <h2 className="text-[20px] font-semibold text-center text-[#1E3A3A] font-manrope leading-[24px]">
                Gửi đến
              </h2>
              <div className="w-7"></div> {/* Empty div for layout balance */}
            </div>

            <div className="px-4 py-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Tìm người"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-9 py-2 bg-white rounded-md text-sm outline-none text-[#1E3A3A]"
                />
                <svg
                  className="absolute left-3 top-2.5"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                    stroke="#1E3A3A"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 14L11 11"
                    stroke="#1E3A3A"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between py-3 cursor-pointer"
                  onClick={() => handleUserToggle(user.id)}
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <span className="text-sm text-[#1E3A3A]">{user.name}</span>
                  </div>
                  <div className="relative">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M21 3.6V20.4C21 20.7314 20.7314 21 20.4 21H3.6C3.26863 21 3 20.7314 3 20.4V3.6C3 3.26863 3.26863 3 3.6 3H20.4C20.7314 3 21 3.26863 21 3.6Z"
                        stroke="#1E3A3A"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill={
                          selectedUsers.includes(user.id) ? "#E06448" : "none"
                        }
                      />
                      {selectedUsers.includes(user.id) && (
                        <path
                          d="M7 12L10 15L17 8"
                          stroke="#F4E7DA"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      )}
                    </svg>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-4 py-4 mt-auto">
              <div
                className="w-[343px] h-[46px] px-4 py-2.5 bg-[#E06448] rounded-full inline-flex justify-center items-center gap-2.5 cursor-pointer mx-auto"
                onClick={handleShareClick}
              >
                <div className="text-center justify-start text-[#F4E7DA] text-sm font-normal font-sora leading-none">
                  Chia sẻ ngay
                </div>
              </div>
            </div>

            <div className="h-1 w-16  rounded-full mx-auto my-4 opacity-20"></div>
          </div>
        ) : (
          <div className="w-full bg-[#FBF6F1]">
            <div className="h-1 w-16  rounded-full mx-auto mt-4 mb-6 opacity-20"></div>
            <div className="w-[100%] px-4 py-2 mb-4 border border-[#FBF6F1]">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={
                    post.author.username === "kiki"
                      ? "/avatars/kiki.svg"
                      : post.author.username === "marnie"
                        ? "/avatars/marnie.svg"
                        : "/avatars/avatar.svg"
                  }
                  alt={post.author.display_name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <h3 className="font-medium text-base text-[#1E3A3A]">
                    {post.author.display_name || "Chihiro Nguyen"}
                  </h3>
                  <div className="flex items-center text-[#6F6F6B] text-xs gap-1.5">
                    <span>{post.author.location || "Washington, DC"}</span>
                    <div className="w-0.5 h-0.5 rounded-full bg-[#6F6F6B]"></div>
                    <span>1d</span>
                    <div className="w-0.5 h-0.5 rounded-full bg-[#6F6F6B]"></div>
                    <span className="flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <g clipPath="url(#clip0_414_13096)">
                          <path
                            d="M7.99992 14.6663C11.6818 14.6663 14.6666 11.6815 14.6666 7.99967C14.6666 4.31777 11.6818 1.33301 7.99992 1.33301C4.31802 1.33301 1.33325 4.31777 1.33325 7.99967C1.33325 11.6815 4.31802 14.6663 7.99992 14.6663Z"
                            stroke="#6F6F6B"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M1.66675 8.33301L5.33342 9.66634L4.66675 11.9997L5.33342 13.9997"
                            stroke="#6F6F6B"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M11.3333 13.6663L10.9999 11.9997L9.33325 11.333V8.99967L11.3333 8.33301L14.3333 8.66634"
                            stroke="#6F6F6B"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12.6667 3.66699L12.3333 4.66699L10 5.00033V7.00033L11.6667 6.33366H13L14.3333 7.00033"
                            stroke="#6F6F6B"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M1.66675 7L3.33341 5.66667L5.00008 5.33333L6.33341 3.33333L5.66675 2"
                            stroke="#6F6F6B"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_414_13096">
                            <rect width="16" height="16" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </span>
                  </div>
                </div>
              </div>

              {/* <p className="text-sm text-[#1E3A3A] mb-4">Hãy nói gì đó về nội dung này</p> */}

              <div className="flex justify-end mb-6">
                <div
                  className="w-[121px] h-[38px] px-4 py-2.5 bg-[#E06448] rounded-full inline-flex justify-center items-center gap-2.5 cursor-pointer"
                  onClick={handleShareClick}
                >
                  <div className="text-center justify-start text-[#F4E7DA] text-sm font-normal font-sora leading-none">
                    Chia sẻ ngay
                  </div>
                </div>
              </div>
            </div>

            <div className="px-4 py-10 border border-[#FBF6F1] ">
              <p className="text-[20px] font-semibold text-[#1E3A3A] mb-4 font-manrope leading-[24px]">
                Gửi đến
              </p>

              <div className="flex gap-4 overflow-x-auto pb-2">
                {mockUsers.slice(0, 6).map((user) => (
                  <div
                    key={user.id}
                    className="flex flex-col items-center min-w-[50px]"
                  >
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-10 h-10 rounded-full object-cover mb-1"
                    />
                    <span className="text-xs text-[#1E3A3A] text-center whitespace-nowrap">
                      {user.name}
                    </span>
                  </div>
                ))}
                <div
                  className="flex flex-col items-center min-w-[50px] cursor-pointer"
                  onClick={() => setShowUserList(true)}
                >
                  <div className="w-10 h-10 rounded-full bg-[#F0F0F0] flex items-center justify-center mb-1">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="5" cy="12" r="2" fill="#1E3A3A" />
                      <circle cx="12" cy="12" r="2" fill="#1E3A3A" />
                      <circle cx="19" cy="12" r="2" fill="#1E3A3A" />
                    </svg>
                  </div>
                  <span className="text-xs text-[#1E3A3A] text-center whitespace-nowrap">
                    Xem thêm
                  </span>
                </div>
              </div>
            </div>

            <div className="h-1 w-16  rounded-full mx-auto mb-4 opacity-20"></div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
